<template>
    <md-sidenav class="md-left" ref="projectSettingsNav">
        <md-dialog-confirm
            :md-title="confirmDuplicateProject.title"
            :md-content-html="getConfirmContentDuplicateProject()"
            :md-ok-text="confirmDuplicateProject.ok"
            :md-cancel-text="confirmDuplicateProject.cancel"
            @close="duplicateProjectConfirm"
            ref="duplicateProjectDialog">
        </md-dialog-confirm>
        <md-dialog-confirm
            :md-title="confirmDuplicateProject.title"
            :md-content-html="getConfirmContentDuplicateLegacyProject()"
            :md-ok-text="confirmDuplicateProject.ok"
            :md-cancel-text="confirmDuplicateProject.cancel"
            @close="duplicateProjectConfirm"
            ref="duplicateProjectLegacyDialog">
        </md-dialog-confirm>
        <md-dialog-confirm
            :md-title="confirm.title"
            :md-content-html="getConfirmContent()"
            :md-ok-text="confirm.ok"
            :md-cancel-text="confirm.cancel"
            @close="deleteProjectConfirm"
            ref="deleteProjectDialog">
        </md-dialog-confirm>
        <md-toolbar class="md-dense main-toolbar">
            <md-button class="md-icon-button" @click="back()">
                <md-icon>menu</md-icon>
            </md-button>
            <h3 class="md-title">Project Settings</h3>
        </md-toolbar>
        <md-layout md-flex="90" md-flex-offset="5" v-if="isAdministrator">
            <form novalidate @submit.stop.prevent="open">
                <h1 class="md-title">Project Name</h1>
                <md-input-container>
                    <md-input v-model="title" :disabled="!activeProjectEntitled"></md-input>
                </md-input-container>
                <md-button class="md-raised md-primary" @click="updateTitle" :disabled="!activeProjectEntitled">Rename</md-button>
            </form>
            <form novalidate @submit.stop.prevent="open" class="project-poster-form">
                <h1 class="md-title" v-if="!project.posterUrl">Add Project Image</h1>
                <h1 class="md-title" v-else>Project Image</h1>
                <div v-if="!project.posterUrl">
                    <md-button class="md-raised md-primary" @click="uploadPoster" :disabled="!activeProjectEntitled">Upload</md-button>
                </div>
                <div v-else>
                    <md-button class="md-raised md-primary" @click="changePoster" :disabled="!activeProjectEntitled">Change</md-button>
                    <md-button class="md-raised md-primary" @click="removePoster" :disabled="!activeProjectEntitled">Remove</md-button>
                </div>
            </form>
        </md-layout>
        <md-layout md-column md-flex="90" md-flex-offset="5" v-else>
            <h1 class="md-title">Project Name</h1>
            <span class="md-body-2">{{ project.title }}</span>
        </md-layout>
        <md-layout md-column md-flex="90" md-flex-offset="5" style="margin-top: 1rem;">
            <h1 class="md-title">Default Cue Placement</h1>
            <form novalidate @submit.stop.prevent="open" class="project-cue-placement-form">
                <div>
                    <md-radio v-model="projectCuePlacement" mdValue="left" class="md-primary" @change="changeCuePlacement"> Left side of page</md-radio><br/>
                    <md-radio v-model="projectCuePlacement" mdValue="right" class="md-primary" @change="changeCuePlacement"> Right side of page</md-radio><br/>
                </div>
            </form>
        </md-layout>
        <!-- The owner always gets the Locked switch, even on a lapsed plan (Josh, 12 Oct 2026); the Duplicate button still needs one. -->
        <md-layout v-if="ownerCanLock" md-column md-flex="90" md-flex-offset="5" style="margin-top: 1rem;">
            <h1 class="md-title">Duplicate Settings</h1>
            <md-switch id="locked" v-model="duplicateSetting" @change="toggleLockedDuplicateSetting" name="locked" style="margin-top: 0px; margin-bottom: 0px;">Locked</md-switch>
            <!-- Locked means owner only, for administrators too: matches the Dashboard
                 menu (kebab-menu.vue canDuplicate) and this text (Josh, 11 Oct 2026). -->
            <p>When project is locked, only the owner can duplicate it. Unlocked projects can be duplicated by all collaborators.</p>
            <md-button v-if="activeProjectEntitled" class="md-raised md-primary" @click="duplicateProject($event)" :disabled="!canCreateProject || duplicateProjectButtonLoading">Duplicate Project</md-button>
        </md-layout>
        <md-layout md-column md-flex="90" md-flex-offset="5" v-if="!project.duplicateSetting && user.uid !== project.ownerUID && project.title !== 'Sample Project'">
            <h1 class="md-title">Duplicate Settings</h1>
            <md-button class="md-raised md-primary" @click="duplicateProject($event)" :disabled="!canCreateProject || duplicateProjectButtonLoading">Duplicate Project</md-button>
        </md-layout>
        <!-- Show Pass (16 Oct 2026): who applied it, or a button to use yours. After Duplicate Settings (Josh, 17 Oct). Plain markup, no new <md-*>. -->
        <div class="showpass-section" v-if="showPassLine || offerShowPass">
            <h1 class="md-title">Show Pass</h1>
            <p class="showpass-line" v-if="showPassLine">{{ showPassLine }}</p>
            <use-show-pass variant="settings"></use-show-pass>
        </div>
        <!-- Plain markup on purpose: no new <md-*> here, so this section does not
             add to the vue-material removal work in Stage D of the Vue 3 migration.
             .noteslist-section reproduces the md-flex="90" md-flex-offset="5"
             geometry the surrounding sections get from md-layout.

             Gate is showNotesListIntegration (owner + feature enabled), NOT
             showNotesListStatus. It must match the load condition in created()
             exactly — that hook already fetches account status, link access and
             the linkable productions for any owner. Gating the panel more
             narrowly than the loader is what left an unlinked project with no
             way to connect: the data was fetched and never rendered.

             The connect / create / disconnect / re-sync controls below were lost
             in the step 11 merge (bbbea4dc) and restored 27 Aug. Every method and
             dialog they use survived the merge intact; only these callers were
             dropped, which is why the feature looked present in code review. -->
        <div class="noteslist-section" v-if="showNotesListIntegration">
            <h1 class="md-title">Show Sync</h1>

            <div v-if="notesListAccountStatusLoading" class="noteslist-link-loading">
                Checking Notes List connection…
            </div>

            <template v-else>
                <p class="md-body-1 noteslist-status-row">
                    <strong>Status:</strong>
                    <span class="noteslist-account-badge" :class="notesListBadgeClass">{{ notesListBadgeLabel }}</span>
                </p>
                <p class="md-body-1" v-if="isNotesListLinked && project.noteslist_production_id">
                    <strong>Production ID:</strong> {{ project.noteslist_production_id }}
                </p>
                <p class="md-body-1" v-if="isNotesListLinked && notesListLinkedAtLabel">
                    <strong>Linked On:</strong> {{ notesListLinkedAtLabel }}
                </p>
                <p class="md-body-1" v-if="isNotesListLinked">
                    <strong>Last sync:</strong> {{ notesListLastSyncLabel }}
                </p>
                <p class="md-body-1 noteslist-sync-error" v-if="notesListSyncError">
                    {{ notesListSyncError }}
                </p>

                <!-- Linked: disconnect / re-sync.
                     Uses production's <md-button> treatment rather than plain buttons —
                     Josh's call on QA, 27 Aug: matching the surrounding panel matters more
                     than sparing this section from the Stage D vue-material removal.
                     The :key forces a re-render when the busy flag flips; vue-material
                     caches the ripple/label and otherwise leaves a stale disabled state.
                     Handler is handleNotesListResyncOrUpgrade, NOT production's bare
                     handleNotesListResync — the sandbox wrapper additionally covers the
                     show-pass and upgrade paths. -->
                <div v-if="isNotesListLinked" class="noteslist-integration-actions">
                    <md-button
                        :key="'nl-disconnect-' + (notesListLinkWorking ? 'busy' : 'idle')"
                        class="md-raised"
                        :disabled="notesListLinkWorking"
                        @click="openNotesListUnlinkDialog">
                        <span class="noteslist-btn-content">
                            Disconnect
                            <span
                                v-if="notesListLinkWorking && notesListActiveAction === 'disconnect'"
                                class="noteslist-btn-spinner"
                                aria-hidden="true"></span>
                        </span>
                    </md-button>
                    <md-button
                        :key="'nl-resync-' + (notesListLinkWorking ? 'busy' : 'idle')"
                        class="md-raised md-primary"
                        :disabled="notesListLinkWorking"
                        @click="handleNotesListResyncOrUpgrade">
                        <span class="noteslist-btn-content">
                            Re-sync
                            <span
                                v-if="notesListLinkWorking && notesListActiveAction === 'resync'"
                                class="noteslist-btn-spinner"
                                aria-hidden="true"></span>
                        </span>
                    </md-button>
                </div>

                <!-- Not linked: pick a production, or create one -->
                <div v-else>
                    <div v-if="notesListLinkAccessLoading" class="noteslist-link-loading">
                        Checking subscription status…
                    </div>
                    <div v-else-if="notesListLinkingDisabled" class="noteslist-subscription-alert" role="alert">
                        <strong>Subscription required</strong>
                        <p class="noteslist-subscription-alert-message">{{ notesListLinkAccessMessage }}</p>
                    </div>
                    <div v-else>
                        <p class="md-body-1 noteslist-connect-copy">
                            Connect this project to a Notes List production to sync cue and script context.
                        </p>
                        <div v-if="notesListProductionsLoading" class="noteslist-link-loading">
                            Loading productions…
                        </div>
                        <div v-else>
                            <div v-if="notesListProductions.length" class="noteslist-production-picker">
                                <label for="noteslist-production-select" class="md-body-1">Choose a production</label>
                                <select
                                    id="noteslist-production-select"
                                    v-model="notesListSelectedProductionId"
                                    class="noteslist-production-select">
                                    <option disabled value="">Select a production...</option>
                                    <option
                                        v-for="production in notesListProductions"
                                        :key="production.id"
                                        :value="production.id">
                                        {{ notesListProductionLabel(production) }}
                                    </option>
                                </select>
                            </div>
                            <p v-else class="md-body-1 noteslist-link-hint">
                                No available unlinked productions found. Create a new one below.
                            </p>
                            <div class="noteslist-connection-actions">
                                <md-button
                                    :key="'nl-connect-' + (notesListLinkWorking ? 'busy' : 'idle')"
                                    class="md-raised md-primary"
                                    :disabled="notesListLinkWorking || !notesListSelectedProductionId"
                                    @click="confirmNotesListLinkInline">
                                    <span class="noteslist-btn-content">
                                        Connect
                                        <span
                                            v-if="notesListLinkWorking && notesListActiveAction === 'connect'"
                                            class="noteslist-btn-spinner"
                                            aria-hidden="true"></span>
                                    </span>
                                </md-button>
                                <p class="noteslist-or-divider">or</p>
                                <md-button
                                    :key="'nl-create-' + (notesListLinkWorking ? 'busy' : 'idle')"
                                    class="md-raised"
                                    :disabled="notesListLinkWorking"
                                    @click="confirmNotesListCreateInline">
                                    <span class="noteslist-btn-content">
                                        Create production
                                        <span
                                            v-if="notesListLinkWorking && notesListActiveAction === 'create'"
                                            class="noteslist-btn-spinner"
                                            aria-hidden="true"></span>
                                    </span>
                                </md-button>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <md-dialog-confirm
            :md-title="deletePagesConfirm.title"
            :md-content-html="getDeletePagesConfirmContent()"
            :md-ok-text="deletePagesConfirm.ok"
            :md-cancel-text="deletePagesConfirm.cancel"
            @close="confirmDeletePages"
            ref="deletePagesDialog">
        </md-dialog-confirm>
        <md-dialog ref="upload-poster-dialog">
            <md-dialog-title>Project Image Upload</md-dialog-title>
            <md-dialog-content>
                <span>JPG, GIF or PNG images no larger than 1MB. For best results, upload a square image.</span>
                <md-input-container>
                    <label for="poster-file-input">Image File</label>
                    <md-file id="poster-file-input" required @selected="onPosterFileChange" accept="image/*"></md-file>
                </md-input-container>
            </md-dialog-content>
            <md-dialog-actions>
                <md-button class="md-primary" @click="cancelSubmitPosterImage()">Cancel</md-button>
                <md-button class="md-primary" :disabled="!validPoster || uploadingPoster" @click="submitPosterImage()">Upload</md-button>
            </md-dialog-actions>
        </md-dialog>
        <md-dialog-confirm
            :md-title="removePosterConfirm.title"
            :md-content-html="removePosterConfirm.description"
            :md-ok-text="removePosterConfirm.ok"
            :md-cancel-text="removePosterConfirm.cancel"
            @close="confirmRemovePoster"
            ref="remove-poster-dialog">
        </md-dialog-confirm>
        <md-dialog ref="noteslist-account-required-dialog">
            <md-dialog-title>Connect your Notes List account</md-dialog-title>
            <md-dialog-content>
                <p class="md-body-1">
                    Link your Notes List account in Account Settings before connecting this project.
                </p>
            </md-dialog-content>
            <md-dialog-actions>
                <md-button class="md-primary" @click="closeNotesListAccountRequiredDialog">Cancel</md-button>
                <md-button class="md-primary" @click="goToAccountSettingsForNotesList">Account settings</md-button>
            </md-dialog-actions>
        </md-dialog>
        <md-dialog-confirm
            class="noteslist-disconnect-dialog"
            :class="{ 'show-md-dark-mode': showMode }"
            md-title="Disconnect from The Notes List?"
            md-content-html="Stops sync between this project and the linked production. Notes in The Notes List are not deleted."
            md-ok-text="Disconnect"
            md-cancel-text="Cancel"
            @close="confirmNotesListUnlink"
            ref="noteslist-unlink-dialog">
        </md-dialog-confirm>
    </md-sidenav>
    </template>
    
    <script>
    import firebase from 'firebase/app';
    import { db } from '@/main';
    import { mapGetters } from 'vuex';
    import NavBus from '@/buses/nav';
    import PDFJS from 'pdfjs-dist/webpack';
    import {PROJECT_DELETE_PAGES_URL, PROJECT_ADD_PAGES_URL, PROJECT_REPLACE_PAGES_URL, NOTESLIST_INTEGRATION_ENABLED} from '@/lib/URLs';
    import { apiAuthHeaders } from '@/lib/api-auth-client';
    import {
        hasNotesListIntegrationAccess,
        fetchNotesListSyncAccess,
        fetchNotesListLinkAccess,
        notesListIntegrationUpgradeMessage,
        notesListCollaboratorOwnerOnlyMessage
    } from '@/lib/noteslist-integration-access';
    import noteslistMarkDark from '@/assets/img/noteslist-mark.png';
    import noteslistMarkLight from '@/assets/img/noteslist-mark-light.png';
    import { canToggleProjectLock } from '@/lib/project-lock';
    import profileByUID from '@/lib/user-profiles';
    import { canOfferShowPass, passAppliedLine } from '@/lib/show-pass-apply';
    import UseShowPass from '@/components/show-pass/use-show-pass';

    PDFJS.workerSrc = '../../../../node_modules/pdfjs-dist/build/pdf.worker.js';

    const ProjectSettingsNav = {
        name: 'CoreHeader',
        props: ['currentPage', 'showMode', 'cuePlacement', 'projectduplicateSettingLocked'],
        data: function() {
            return {
                nightMode: false,
                title: '',
                deplicateProjectHide: this.$props.collabDuplicateProjectLocked || false,
                confirmDuplicateProject: {
                    title: 'Confirm Project Duplication',
                    ok: 'Yes',
                    cancel: 'Cancel'
                },
                projectCollectionRef: db.collection('projects'),
                confirm: {
                    title: 'Confirm Project Delete',
                    ok: 'yes',
                    cancel: 'Cancel'
                },
                removePosterConfirm: {
                    title: 'Remove Image',
                    description: 'Warning, you are about to remove your project image! This can\'t be undone. Please confirm you would like to delete this image.',
                    ok: 'Yes',
                    cancel: 'Cancel'
                },
                delete_page_from: this.currentPage,
                delete_page_to: this.currentPage,
                addPageFile: null,
                addPageFilePagesCount: 0,
                add_page_where: 'before',
                add_page_into: this.currentPage,
                replacePageFile: null,
                replacePageFilePagesCount: 0,
                replace_page_from: this.currentPage,
                replace_page_to: this.currentPage,
                working: false,
                posterImage: null,
                validPoster: false,
                uploadingPoster: false,
                projectCuePlacement: this.$props.cuePlacement || 'right',
                duplicateSetting: this.$props.projectduplicateSettingLocked || false,
                duplicateProjectButtonLoading: false,
                notesListSelectedProductionId: '',
                notesListProductions: [],
                notesListProductionsLoading: false,
                notesListLinkWorking: false,
                notesListActiveAction: null,
                notesListAccountStatus: { linkStatus: 'disconnected' },
                notesListAccountStatusLoading: false,
                lastToastedNotesListSyncError: null,
                syncAccessAllowed: false,
                notesListLinkAccessLoading: false,
                notesListLinkAccessStatus: null,
                passOwnerName: null
            };
        },
        computed: {
            ...mapGetters([
                'project',
                'user',
                'collaboratorByUID',
                'currentVersion',
                'ownerUID',
                'collaborators',
                'cues',
                'notes',
                'bookmarks',
                'projectTotalPages',
                'activeProjectEntitled',
                'canCreateProject'
            ]),
            offerShowPass() {
                return canOfferShowPass(this.user, this.project);
            },
            // Only for a pass applied through Cuelist (passOwnerUID); a Notes List show
            // pass also sets unlocked and is described in Show Sync instead.
            showPassLine() {
                const p = this.project || {};
                if (!p.unlocked || !p.passOwnerUID) return '';
                return passAppliedLine(this.passOwnerName, p.passAppliedAt);
            },
            ownerCanLock() {
                return canToggleProjectLock(this.user, this.project);
            },
            // --- Show Sync (Notes List) status -------------------------------
            // Read-only. Everything here comes off the project doc's noteslist_*
            // fields, which staging-v2-main's integration writes. No API calls,
            // so this renders correctly on the sandbox, which has no Notes List
            // credentials. The connect/disconnect/resync controls on staging are
            // deliberately NOT ported yet — they need partner credentials.
            isNotesListLinked() {
                // Must match staging's definition exactly, or the two builds will
                // disagree about what "connected" means after the merge.
                return this.project.noteslist_link_status === 'linked'
                    && !!this.project.noteslist_production_id;
            },
            notesListSyncError() {
                return (this.project && this.project.noteslist_last_sync_error) || '';
            },
            showNotesListStatus() {
                // Hidden entirely until the project has some Notes List state, so
                // the sandbox (and any unlinked project) is not given a dead panel.
                return !!(this.project
                    && (this.project.noteslist_link_status
                        || this.project.noteslist_production_id));
            },
            notesListBadgeLabel() {
                if (this.notesListSyncError) {
                    return 'Sync error';
                }
                return this.isNotesListLinked ? 'Connected' : 'Not connected';
            },
            notesListBadgeClass() {
                if (this.notesListSyncError) {
                    return 'is-error';
                }
                return this.isNotesListLinked ? 'is-linked' : 'is-disconnected';
            },
            projectRef: function() {
                return db.collection('projects').doc(this.project.id);
            },
            userProjectData: function() {
                return this.user.projects[this.project.id];
            },
            deletePagesConfirm: function() {
                return {
                    title: 'Delete Page' + (Number(this.delete_page_from) === Number(this.delete_page_to) ? '' : 's') + '?',
                    ok: 'yes',
                    cancel: 'Cancel'
                };
            },
            versionID: function() {
                return this.currentVersion ? this.currentVersion.id : '';
            },
            isAdministrator() {
                const collab = this.collaboratorByUID(this.user.uid);
                return this.ownerUID === this.user.uid || (collab && collab.type === 'administrator');
            },
            showNotesListIntegration() {
                // Always show for owners when enabled. Gating on account status
                // caused a ~1s flash then hide while status resolved.
                return (
                    NOTESLIST_INTEGRATION_ENABLED &&
                    this.user &&
                    this.project &&
                    this.user.uid === this.project.ownerUID
                );
            },
            isNotesListAccountLinked() {
                return this.notesListAccountStatus.linkStatus === 'linked';
            },
            showNotesListCollaboratorNote() {
                return (
                    NOTESLIST_INTEGRATION_ENABLED &&
                    this.user &&
                    this.project &&
                    this.user.uid !== this.project.ownerUID
                );
            },
            notesListCollaboratorOwnerOnlyMessage() {
                return notesListCollaboratorOwnerOnlyMessage();
            },
            noteslistLogoSrc() {
                return this.showMode ? noteslistMarkDark : noteslistMarkLight;
            },
            notesListLinkingDisabled() {
                return !!(this.notesListLinkAccessStatus && this.notesListLinkAccessStatus.allowed === false);
            },
            notesListLinkAccessMessage() {
                return (this.notesListLinkAccessStatus && this.notesListLinkAccessStatus.message) || '';
            },
            notesListLinkedAtLabel() {
                const date = this.noteslistTimestampToDate(this.project.noteslist_linked_at);
                return date ? date.toLocaleString() : '';
            },
            notesListLastSyncLabel() {
                const date = this.noteslistTimestampToDate(this.project.noteslist_last_sync_at);
                if (!date) {
                    return 'Never';
                }
                return `${this.formatNotesListSyncAge(date)} (${date.toLocaleString()})`;
            },
            totalPages: function() {
                if (this.versionID) {
                    return this.projectTotalPages || 0;
                }
                
                return (this.project && Array.isArray(this.project.pdfPageNumbers)) 
                    ? this.project.pdfPageNumbers.length 
                    : 0;
            }
        },
        watch: {
            'project.passOwnerUID': {
                immediate: true,
                handler(uid) {
                    this.passOwnerName = null;
                    if (!uid) return;
                    profileByUID(uid).then((profile) => {
                        if (this.project && this.project.passOwnerUID === uid) {
                            this.passOwnerName = (profile && profile.username) || null;
                        }
                    });
                }
            },
            currentPage: function(val) {
                this.delete_page_from = val;
                this.delete_page_to = val;
                this.add_page_into = val;
                this.replace_page_from = val;
                this.replace_page_to = val;
            },
            isNotesListLinked: {
                immediate: true,
                handler(val) {
                    if (val) {
                        this.refreshSyncAccess();
                    } else {
                        this.syncAccessAllowed = false;
                    }
                }
            },
            'project.noteslist_last_sync_error': function(newVal) {
                if (newVal && newVal !== this.lastToastedNotesListSyncError) {
                    this.$toast.error(newVal);
                    this.lastToastedNotesListSyncError = newVal;
                }
                if (!newVal) {
                    this.lastToastedNotesListSyncError = null;
                }
            }
        },
        methods: {
            formatNotesListSyncAge(date) {
                const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
                if (seconds < 60) {
                    return 'just now';
                }
                const minutes = Math.floor(seconds / 60);
                if (minutes < 60) {
                    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
                }
                const hours = Math.floor(minutes / 60);
                if (hours < 24) {
                    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
                }
                const days = Math.floor(hours / 24);
                return `${days} day${days === 1 ? '' : 's'} ago`;
            },
            // Copied verbatim from staging-v2-main so the two builds cannot drift
            // in how they read Notes List timestamps. Do not "tidy" this — the
            // {_seconds, _nanoseconds} branch is load-bearing.
            noteslistTimestampToDate(value) {
                if (!value) {
                    return null;
                }
                if (typeof value.toDate === 'function') {
                    return value.toDate();
                }
                // Express has no toJSON hook for Firestore Admin Timestamps, so they
                // cross the wire as this plain {_seconds, _nanoseconds} shape.
                // eslint-disable-next-line no-underscore-dangle
                if (typeof value === 'object' && typeof value._seconds === 'number') {
                    // eslint-disable-next-line no-underscore-dangle
                    return new Date((value._seconds * 1000) + Math.floor((value._nanoseconds || 0) / 1e6));
                }
                const date = new Date(value);
                return Number.isNaN(date.getTime()) ? null : date;
            },
            extractFilePathFromDocumentUrl() {
                let filePath = '';
                try {
                    const url = new URL(this.project.documentUrl);
                    const afterO = url.pathname.split('/o/')[1] || '';
                    filePath = decodeURIComponent(afterO);
                } catch (e) {
                    console.error('Error extracting file path from documentUrl:', e);
                    filePath = '';
                }
                return filePath;
            },
            toggleLockedDuplicateSetting() {
                this.$store.dispatch('updatelockDuplicateSettingProject', { duplicateSetting: !this.duplicateSetting });
            },
            updateTitle() {
                this.projectRef.update({title: this.title});
            },
            closeProjectSettingsNav() {
                this.$refs.projectSettingsNav.close();
            },
            back() {
                this.closeProjectSettingsNav();
                NavBus.$emit('main-nav.open');
            },
            getConfirmContent() {
                const numCollaborators = this.collaborators.length;
                const numCues = this.cues.length;
                return `Warning, you are about to delete a project with <strong>${numCollaborators}</strong> collaborators and <strong>${numCues}</strong> cues already written! Please confirm you would like to delete this project.`;
            },
            deleteProject() {
                this.$refs.deleteProjectDialog.open();
            },
            deleteProjectConfirm(type) {
                if (type === 'ok') {
                    this.$store.dispatch('deleteProject', this.project.id);
                    this.$router.replace('/dashboard');
                }
            },
            addPages() {
                if (this.addPageFile && this.addPageFilePagesCount) {
                    this.working = true;
                    this.doUploadFileToAddPages();
                }
            },
            getConfirmContentDuplicateProject() {
                return `
                    <div>
                        <p>Please confirm you would like to duplicate this project. The following will be copied:</p>
                        <ul>
                            <li>Project settings.</li>
                            <li>Layer information.</li>
                            <li>Cues and all annotations.</li>
                            <li>Structure marks (Act, Scene and Song).</li>
                            <li>The ownership of all layers in the new project will be transferred to the user duplicating the project.</li>
                        </ul>
                    </div>
                `;
            },
            getConfirmContentDuplicateLegacyProject() {
                return `
                    <div>
                        <p>Please confirm you would like to duplicate this legacy project. The following will be duplicated:</p>
                        <ul>
                            <li>Project settings.</li>
                            <li>Layer information.</li>
                            <li>Cues and all annotations.</li>
                            <li>Structure marks (Act, Scene and Song).</li>
                            <li>The ownership of all layers in the new project will be transferred to the user duplicating the project.</li>
                        </ul>
                        <p>
                            <span>
                                <b>Note:</b>
                                <span>
                                    This action will create a new project using the latest version of Cuelist. Some annotations from your legacy project may not 
                                    transfer correctly to this new version. Please double check all cues and notes on your new project to confirm their accuracy and completeness.
                                    The original version of the project will not be altered during the process.
                                </span>
                            </span>
                        </p>
                    </div>
                `;
            },
            duplicateProject(e) {
                if (this.project.isNew) {
                    this.$refs.duplicateProjectDialog.open();
                } else {
                    this.$refs.duplicateProjectLegacyDialog.open();
                }
                e.stopPropagation();
                e.preventDefault();
            },
            // One duplicate for the whole app (Josh, 12 Oct 2026): this button and the
            // dashboard menu both run the duplicateProject action in vuex/actions.js, so
            // what a copy keeps or drops (Show Pass, Notes List links, structure marks)
            // can never differ by entry point again. It used to be a second, drifting copy.
            async duplicateProjectConfirm(type) {
                if (type !== 'ok') {
                    return;
                }
                this.duplicateProjectButtonLoading = true;
                this.$toast.success('Project duplication process started in background!.');
                try {
                    await this.$store.dispatch('duplicateProject', { project: this.project });
                    this.$toast.success('Project duplicated successfully!');
                } catch (error) {
                    console.error('Error duplicating project:', error);
                    this.$toast.error('Could not duplicate the project. Please try again.');
                } finally {
                    this.$store.dispatch('setDuplicateProjectLoading', false);
                    this.duplicateProjectButtonLoading = false;
                }
            },
            doUploadFileToAddPages() {
                const filePath = this.extractFilePathFromDocumentUrl();
    
                const sendRequest = apiAuthHeaders(PROJECT_ADD_PAGES_URL).then(headers => new Promise((resolve, reject) => {
                    const formData = new FormData();
                    formData.append('project_id', this.project.id);
                    formData.append('documentUrl', this.project.documentUrl);
                    formData.append('filePath', filePath);
                    formData.append('add_page_into', this.add_page_into);
                    formData.append('add_page_where', this.add_page_where);
                    formData.append('totalPages', this.totalPages);
                    formData.append('file', this.addPageFile, this.addPageFile.name);
                    const req = new XMLHttpRequest();
                    req.open('POST', PROJECT_ADD_PAGES_URL);
                    Object.keys(headers).forEach(name => req.setRequestHeader(name, headers[name]));
                    req.send(formData);
                    req.onload = () => {
                        if (req.status === 200) {
                            resolve(req.response);
                        } else {
                            reject(req.response);
                        }
                    };
                }));
                sendRequest.then(() => {
                    for (const cue of this.cues) {
                        if ((cue.pageNumber === Number(this.add_page_into) && this.add_page_where === 'before') || cue.pageNumber > Number(this.add_page_into)) {
                            cue.pageNumber += this.addPageFilePagesCount;
                            this.$store.dispatch('updateCue', cue);
                        }
                    }
                    for (const note of this.notes) {
                        if ((note.page === Number(this.add_page_into) && this.add_page_where === 'before') || note.page > Number(this.add_page_into)) {
                            note.page += this.addPageFilePagesCount;
                            this.$store.dispatch('updateUserNote', {note});
                        }
                    }
                    for (const bookmarkPage of Object.keys(this.bookmarks)) {
                        if ((Number(bookmarkPage) === Number(this.add_page_into) && this.add_page_where === 'before') || bookmarkPage > this.add_page_into) {
                            this.$store.dispatch('moveBookmark', {
                                from: bookmarkPage,
                                to: Number(bookmarkPage) + Number(this.addPageFilePagesCount)
                            });
                        }
                    }
                    const shiftFromPage = this.add_page_where === 'before'
                        ? Number(this.add_page_into)
                        : Number(this.add_page_into) + 1;
                    this.$store.dispatch('shiftScriptMarkersAfterPage', {
                        fromPage: shiftFromPage,
                        delta: Number(this.addPageFilePagesCount)
                    });
                    this.$store.dispatch('notifyNotesListStructureIfLinked');
                    const newPagePosition = Number(this.add_page_into) + (this.add_page_where === 'after' ? 1 : 0);
                    NavBus.$emit('reloadCurrentProject', {newPagePosition});
                    this.addPageFile = null;
                    this.addPageFilePagesCount = 0;
                    this.working = false;
                }).catch(() => {
                    this.addPageFile = null;
                    this.addPageFilePagesCount = 0;
                    this.working = false;
                });
            },
            onAddPageFileChange: async function(fileList) {
                this.addPageFile = fileList[0];
                if (this.addPageFile) {
                    const arrayBuffer = await this.addPageFile.arrayBuffer();
                    PDFJS.getDocument({data: new Uint8Array(arrayBuffer)}).then((pdf) => {
                        this.addPageFilePagesCount = pdf.pdfInfo.numPages;
                    });
                }
            },
            replacePages() {
                if (this.replacePageFile && this.replacePageFilePagesCount) {
                    this.working = true;
                    this.doUploadFileToReplacePages();
                }
            },
            doUploadFileToReplacePages() {
                const filePath = this.extractFilePathFromDocumentUrl();
    
                const sendRequest = apiAuthHeaders(PROJECT_REPLACE_PAGES_URL).then(headers => new Promise((resolve, reject) => {
                    const formData = new FormData();
                    formData.append('project_id', this.project.id);
                    formData.append('documentUrl', this.project.documentUrl);
                    formData.append('filePath', filePath);
                    formData.append('replace_page_from', this.replace_page_from);
                    formData.append('replace_page_to', this.replace_page_to);
                    formData.append('totalPages', this.totalPages);
                    formData.append('file', this.replacePageFile, this.replacePageFile.name);
                    const req = new XMLHttpRequest();
                    req.open('POST', PROJECT_REPLACE_PAGES_URL);
                    Object.keys(headers).forEach(name => req.setRequestHeader(name, headers[name]));
                    req.send(formData);
                    req.onload = () => {
                        if (req.status === 200) {
                            resolve(req.response);
                        } else {
                            reject(req.response);
                        }
                    };
                }));
                sendRequest.then(() => {
                    const replacingPages = (Number(this.replace_page_to) - Number(this.replace_page_from)) + 1;
                    for (const cue of this.cues) {
                        if (replacingPages < this.replacePageFilePagesCount) {
                            if (cue.pageNumber > Number(this.replace_page_to)) {
                                cue.pageNumber += this.replacePageFilePagesCount - replacingPages;
                                this.$store.dispatch('updateCue', cue);
                            }
                        } else if (replacingPages > this.replacePageFilePagesCount) {
                            if ((cue.pageNumber <= Number(this.replace_page_to)) && (cue.pageNumber >= Number(this.replace_page_from) + this.replacePageFilePagesCount)) {
                                this.$store.dispatch('deleteCue', cue);
                            }
                            if (cue.pageNumber > Number(this.replace_page_to)) {
                                cue.pageNumber -= replacingPages - this.replacePageFilePagesCount;
                                this.$store.dispatch('updateCue', cue);
                            }
                        }
                    }
                    for (const note of this.notes) {
                        if (replacingPages < this.replacePageFilePagesCount) {
                            if (note.page > Number(this.replace_page_to)) {
                                note.page += this.replacePageFilePagesCount - replacingPages;
                                this.$store.dispatch('updateUserNote', {note});
                            }
                        } else if (replacingPages > this.replacePageFilePagesCount) {
                            if ((note.page <= Number(this.replace_page_to)) && (note.page >= Number(this.replace_page_from) + this.replacePageFilePagesCount)) {
                                this.$store.dispatch('removeUserNote', {note});
                            }
                            if (note.page > Number(this.replace_page_to)) {
                                note.page -= replacingPages - this.replacePageFilePagesCount;
                                this.$store.dispatch('updateUserNote', {note});
                            }
                        }
                    }
                    for (const bookmarkPage of Object.keys(this.bookmarks)) {
                        if (replacingPages < this.replacePageFilePagesCount) {
                            if (Number(bookmarkPage) > Number(this.replace_page_to)) {
                                this.$store.dispatch('moveBookmark', {
                                    from: bookmarkPage,
                                    to: Number(bookmarkPage) + Number(this.replacePageFilePagesCount - replacingPages)
                                });
                            }
                        } else if (replacingPages > this.replacePageFilePagesCount) {
                            if ((Number(bookmarkPage) <= Number(this.replace_page_to)) && (Number(bookmarkPage) >= Number(this.replace_page_from) + this.replacePageFilePagesCount)) {
                                this.$store.dispatch('removeBookmark', {
                                    pageNumber: bookmarkPage
                                });
                            }
                            if (Number(bookmarkPage) > Number(this.replace_page_to)) {
                                this.$store.dispatch('moveBookmark', {
                                    from: bookmarkPage,
                                    to: Number(bookmarkPage) - Number(replacingPages - this.replacePageFilePagesCount)
                                });
                            }
                        }
                    }
                    this.$store.dispatch('adjustScriptMarkersAfterPageReplace', {
                        replaceFrom: this.replace_page_from,
                        replaceTo: this.replace_page_to,
                        newPageCount: this.replacePageFilePagesCount
                    });
                    this.$store.dispatch('notifyNotesListStructureIfLinked');
                    const newPagePosition = Number(this.replace_page_from);
                    NavBus.$emit('reloadCurrentProject', {newPagePosition});
                    this.replacePageFile = null;
                    this.replacePageFilePagesCount = 0;
                    this.working = false;
                }).catch(() => {
                    this.replacePageFile = null;
                    this.replacePageFilePagesCount = 0;
                    this.working = false;
                });
            },
            onReplacePageFileChange: async function(fileList) {
                this.replacePageFile = fileList[0];
                if (this.replacePageFile) {
                    const arrayBuffer = await this.replacePageFile.arrayBuffer();
                    PDFJS.getDocument({data: new Uint8Array(arrayBuffer)}).then((pdf) => {
                        this.replacePageFilePagesCount = pdf.pdfInfo.numPages;
                    });
                }
            },
            deletePages() {
                this.$refs.deletePagesDialog.open();
            },
            doDeletePages() {
                const filePath = this.extractFilePathFromDocumentUrl();
    
                this.$http.post(PROJECT_DELETE_PAGES_URL, {
                    project_id: this.project.id,
                    documentUrl: this.project.documentUrl,
                    filePath: filePath,
                    delete_page_from: this.delete_page_from,
                    delete_page_to: this.delete_page_to,
                    totalPages: this.totalPages
                }).then((response) => {
                    if (!response.body.success) {
                        const error = new Error('Delete pages failed');
                        error.success = false;
                        throw error;
                    }
                    for (const cue of this.cues) {
                        if (cue.pageNumber >= Number(this.delete_page_from) && cue.pageNumber <= Number(this.delete_page_to)) {
                            this.$store.dispatch('deleteCue', cue);
                        } else if (cue.pageNumber > Number(this.delete_page_to)) {
                            cue.pageNumber -= (Number(this.delete_page_to) - Number(this.delete_page_from)) + 1;
                            this.$store.dispatch('updateCue', cue);
                        }
                    }
                    for (const note of this.notes) {
                        if (note.page >= Number(this.delete_page_from) && note.page <= Number(this.delete_page_to)) {
                            this.$store.dispatch('removeUserNote', {note});
                        } else if (note.page > Number(this.delete_page_to)) {
                            note.page -= (Number(this.delete_page_to) - Number(this.delete_page_from)) + 1;
                            this.$store.dispatch('updateUserNote', {note});
                        }
                    }
                    for (const bookmarkPage of Object.keys(this.bookmarks)) {
                        if (Number(bookmarkPage) >= Number(this.delete_page_from) && Number(bookmarkPage) <= Number(this.delete_page_to)) {
                            this.$store.dispatch('removeBookmark', {
                                pageNumber: bookmarkPage
                            });
                        } else if (Number(bookmarkPage) > Number(this.delete_page_to)) {
                            this.$store.dispatch('moveBookmark', {
                                from: bookmarkPage,
                                to: Number(bookmarkPage) - ((Number(this.delete_page_to) - Number(this.delete_page_from)) + 1)
                            });
                        }
                    }
    
                    // Act/Scene/Song markers: drop those on removed pages, shift later ones.
                    return this.$store.dispatch('adjustScriptMarkersAfterPageDelete', {
                        fromPage: Number(this.delete_page_from),
                        toPage: Number(this.delete_page_to)
                    }).then(() => {
                        this.$store.dispatch('notifyNotesListStructureIfLinked');

                        let newPagePosition = this.currentPage;
                        const newTotalPages = Number(this.totalPages) - ((Number(this.delete_page_to) - Number(this.delete_page_from)) + 1);
                        if (this.currentPage > Number(this.delete_page_to)) {
                            newPagePosition -= (Number(this.delete_page_to) - Number(this.delete_page_from)) + 1;
                        }
                        newPagePosition = Math.min(newPagePosition, newTotalPages);

                        NavBus.$emit('reloadCurrentProject', {newPagePosition});
                        this.working = false;
                        return {success: true};
                    });
                }).catch(() => {
                    this.working = false;
                });
            },
            confirmDeletePages(type) {
                if (type === 'ok') {
                    this.working = true;
                    this.doDeletePages();
                }
            },
            getDeletePagesConfirmContent() {
                if (Number(this.delete_page_from) === Number(this.delete_page_to)) {
                    return `Warning, you are about to delete page <strong>${this.delete_page_from}</strong>. All cues and text notes on this page will also be deleted. Please confirm you would like to delete this page.`;
                }
                return `Warning, you are about to delete pages <strong>${this.delete_page_from}</strong> to <strong>${this.delete_page_to}</strong>. All cues and text notes on these pages will also be deleted. Please confirm you would like to delete these pages.`;
            },
            uploadPoster() {
                this.closeProjectSettingsNav();
                this.$refs['upload-poster-dialog'].open();
            },
            onPosterFileChange: function(fileList) {
                this.posterImage = fileList[0];
                if (this.posterImage.size > 1024 * 1024) {
                    this.posterImage = null;
                    this.validPoster = false;
                } else {
                    this.validPoster = true;
                }
            },
            submitPosterImage() {
                if (this.validPoster && this.posterImage) {
                    this.uploadingPoster = true;
                    const fileReader = new FileReader();
                    fileReader.addEventListener('load', () => {
                        const path = 'posters/' + this.project.id + '.png';
                        const storageRef = firebase.storage().ref();
                        storageRef.child(path).putString(fileReader.result, 'data_url').then((snapshot) => {
                            snapshot.ref.getDownloadURL().then((downloadURL) => {
                                this.projectRef.update({posterUrl: downloadURL});
                                this.cancelSubmitPosterImage();
                            });
                        });
                    });
                    fileReader.readAsDataURL(this.posterImage);
                }
            },
            toggleDuplicateProject() {
                this.$nextTick(() => {
                    db.collection('projects').doc(this.project.id).update({
                        collabDuplicateProjectLocked: this.deplicateProjectHide
                    });
                });
            },
            changeCuePlacement() {
                this.$nextTick(() => {
                    db.collection('users').doc(this.user.uid).update({
                        ['projects.' + this.project.id + '.cuePlacement']: this.projectCuePlacement
                    });
                });
            },
            changePoster() {
                this.closeProjectSettingsNav();
                this.$refs['upload-poster-dialog'].open();
            },
            removePoster() {
                this.closeProjectSettingsNav();
                this.$refs['remove-poster-dialog'].open();
            },
            confirmRemovePoster(type) {
                if (type === 'ok') {
                    const path = 'posters/' + this.project.id + '.png';
                    const storageRef = firebase.storage().ref();
                    storageRef.child(path).delete().then(() => {
                        this.projectRef.update({posterUrl: ''});
                    });
                }
            },
            cancelSubmitPosterImage() {
                this.uploadingPoster = false;
                this.validPoster = false;
                this.posterImage = null;
                this.$refs['upload-poster-dialog'].close();
            },
            async loadLinkableNotesListProductionsInline() {
                this.notesListProductionsLoading = true;
                try {
                    const result = await this.$store.dispatch('fetchLinkableNotesListProductions');
                    this.notesListProductions = this.normalizeNotesListProductions(result);
                } catch (error) {
                    this.$toast.error(error.message || 'Failed to load productions');
                } finally {
                    this.notesListProductionsLoading = false;
                }
            },
            closeNotesListAccountRequiredDialog() {
                this.$refs['noteslist-account-required-dialog'].close();
            },
            goToAccountSettingsForNotesList() {
                this.closeNotesListAccountRequiredDialog();
                this.closeProjectSettingsNav();
                this.$router.push('/account');
            },
            normalizeNotesListProductions(result) {
                if (!result) {
                    return [];
                }
                if (Array.isArray(result)) {
                    return result;
                }
                if (Array.isArray(result.productions)) {
                    return result.productions;
                }
                if (result.productions && Array.isArray(result.productions.productions)) {
                    return result.productions.productions;
                }
                return [];
            },
            notesListProductionLabel(production) {
                if (!production || typeof production !== 'object') {
                    return 'Unnamed production';
                }
                const name = production.name || production.title || production.id || 'Unnamed production';
                if (production.short_code) {
                    return `${name} (${production.short_code})`;
                }
                return name;
            },
            applyNotesListLinkResult(result) {
                if (!result) {
                    return;
                }
                const linkedAt = this.noteslistTimestampToDate(result.linkedAt);
                const lastSyncAt = this.noteslistTimestampToDate(result.lastSyncAt);
                this.$store.dispatch('patchActiveProjectFields', {
                    noteslist_link_status: result.linkStatus,
                    noteslist_production_id: result.productionId,
                    noteslist_linked_at: linkedAt ? linkedAt.toISOString() : null,
                    noteslist_last_sync_at: lastSyncAt ? lastSyncAt.toISOString() : null,
                    noteslist_last_sync_error: result.lastSyncError,
                    noteslist_webhook_url: result.webhookUrl
                });
            },
            async confirmNotesListLinkInline() {
                if (!this.notesListSelectedProductionId) {
                    this.$toast.error('Select a production from the list');
                    return;
                }
                if (this.notesListLinkingDisabled) {
                    this.$toast.error(this.notesListLinkAccessMessage);
                    return;
                }
                this.notesListLinkWorking = true;
                this.notesListActiveAction = 'connect';
                try {
                    const status = await this.$store.dispatch('fetchNotesListAccountStatus');
                    if (status.linkStatus !== 'linked') {
                        this.$refs['noteslist-account-required-dialog'].open();
                        return;
                    }
                    const result = await this.$store.dispatch('linkNotesListProject', {
                        projectId: this.project.id,
                        mode: 'link_existing',
                        productionId: this.notesListSelectedProductionId
                    });
                    // Clear busy before Connected UI mounts — md-button can stick
                    // disabled if it first renders with :disabled="true".
                    this.notesListLinkWorking = false;
                    this.notesListActiveAction = null;
                    this.applyNotesListLinkResult(result);
                    this.$toast.success('Project linked to The Notes List');
                } catch (error) {
                    if (error.code === 'ACCOUNT_NOT_LINKED') {
                        this.$refs['noteslist-account-required-dialog'].open();
                        return;
                    }
                    this.$toast.error(error.message || 'Failed to link project');
                } finally {
                    this.notesListLinkWorking = false;
                    this.notesListActiveAction = null;
                }
            },
            handleNotesListUpgradeClick() {
                this.$toast.info(notesListIntegrationUpgradeMessage());
                if (this.$refs.upgradeAcct) {
                    this.$refs.upgradeAcct.open({ preferDual: true });
                } else {
                    this.$router.push('/account');
                }
            },
            async loadNotesListLinkAccess() {
                this.notesListLinkAccessLoading = true;
                try {
                    this.notesListLinkAccessStatus = await fetchNotesListLinkAccess();
                } finally {
                    this.notesListLinkAccessLoading = false;
                }
            },
            async refreshSyncAccess() {
                if (!this.isNotesListLinked) {
                    this.syncAccessAllowed = false;
                    return;
                }
                const status = await fetchNotesListSyncAccess(this.project && this.project.id);
                this.syncAccessAllowed = !!(status && status.allowed);
            },
            handleNotesListResyncOrUpgrade() {
                if (this.project.noteslist_show_pass_active) {
                    this.handleNotesListResync();
                    return;
                }
                if (!this.hasNotesListIntegrationAccess && !this.syncAccessAllowed) {
                    this.refreshSyncAccess().then(() => {
                        if (this.syncAccessAllowed) {
                            this.handleNotesListResync();
                        } else {
                            this.handleNotesListUpgradeClick();
                        }
                    });
                    return;
                }
                this.handleNotesListResync();
            },
            async confirmNotesListCreateInline() {
                if (this.notesListLinkingDisabled) {
                    this.$toast.error(this.notesListLinkAccessMessage);
                    return;
                }
                // Prefer linking an existing same-named production over creating a duplicate.
                const projectTitle = ((this.project && this.project.title) || '').trim().toLowerCase();
                if (projectTitle && this.notesListProductions.length) {
                    const match = this.notesListProductions.find((p) => {
                        const name = ((p && (p.name || p.title)) || '').trim().toLowerCase();
                        return name && name === projectTitle;
                    });
                    if (match && match.id) {
                        this.notesListSelectedProductionId = match.id;
                        this.$toast.info(
                            'A Notes List production with this project name already exists. Select it above and click Connect instead of creating another.'
                        );
                        return;
                    }
                }
                this.notesListLinkWorking = true;
                this.notesListActiveAction = 'create';
                try {
                    const status = await this.$store.dispatch('fetchNotesListAccountStatus');
                    if (status.linkStatus !== 'linked') {
                        this.$refs['noteslist-account-required-dialog'].open();
                        return;
                    }
                    const result = await this.$store.dispatch('linkNotesListProject', {
                        projectId: this.project.id,
                        mode: 'create'
                    });
                    // Clear busy before Connected UI mounts — md-button can stick
                    // disabled if it first renders with :disabled="true".
                    this.notesListLinkWorking = false;
                    this.notesListActiveAction = null;
                    this.applyNotesListLinkResult(result);
                    this.$toast.success('Project linked to The Notes List');
                } catch (error) {
                    this.$toast.error(error.message || 'Failed to create production');
                } finally {
                    this.notesListLinkWorking = false;
                    this.notesListActiveAction = null;
                }
            },
            openNotesListUnlinkDialog() {
                this.$refs['noteslist-unlink-dialog'].open();
            },
            async confirmNotesListUnlink(type) {
                if (type !== 'ok') {
                    return;
                }
                this.notesListLinkWorking = true;
                this.notesListActiveAction = 'disconnect';
                try {
                    const result = await this.$store.dispatch('unlinkNotesListProject', {
                        projectId: this.project.id
                    });
                    this.applyNotesListLinkResult(result);
                    this.$toast.success('Disconnected from The Notes List');
                    // Re-check subscription status for the next connection —
                    // it may be stale by the time the user tries to relink.
                    this.loadNotesListLinkAccess();
                    if (this.isNotesListAccountLinked) {
                        this.loadLinkableNotesListProductionsInline();
                    }
                } catch (error) {
                    this.$toast.error(error.message || 'Failed to disconnect');
                } finally {
                    this.notesListLinkWorking = false;
                    this.notesListActiveAction = null;
                }
            },
            async handleNotesListResync() {
                this.notesListLinkWorking = true;
                this.notesListActiveAction = 'resync';
                try {
                    await this.$store.dispatch('resyncNotesListProject', {
                        projectId: this.project.id
                    });
                    this.$store.dispatch('patchActiveProjectFields', {
                        noteslist_last_sync_at: new Date().toISOString(),
                        noteslist_last_sync_error: null
                    });
                    this.$toast.success('Re-sync completed with The Notes List');
                } catch (error) {
                    this.$toast.error(error.message || 'Re-sync failed');
                } finally {
                    this.notesListLinkWorking = false;
                    this.notesListActiveAction = null;
                }
            },
            async loadNotesListAccountStatus() {
                this.notesListAccountStatusLoading = true;
                try {
                    this.notesListAccountStatus = await this.$store.dispatch('fetchNotesListAccountStatus');
                } catch (error) {
                    if (error.code === 'ACCOUNT_NOT_LINKED') {
                        this.notesListAccountStatus = { linkStatus: 'disconnected' };
                    } else {
                        this.$toast.error(error.message || 'Failed to load Notes List account status');
                    }
                } finally {
                    this.notesListAccountStatusLoading = false;
                }
            },
            maybeToastNotesListSyncError() {
                const err = this.project && this.project.noteslist_last_sync_error;
                if (!err || err === this.lastToastedNotesListSyncError) {
                    return;
                }
                this.$toast.error(err);
                this.lastToastedNotesListSyncError = err;
            }
        },
        created: function() {
            NavBus.$on('project-settings-nav.open', async () => {
                if (this.$refs.projectSettingsNav) {
                    this.title = this.project.title;
                    this.notesListLinkWorking = false;
                    this.notesListActiveAction = null;
                    this.$refs.projectSettingsNav.open();
                    this.maybeToastNotesListSyncError();
                    if (NOTESLIST_INTEGRATION_ENABLED && this.user && this.project && this.user.uid === this.project.ownerUID) {
                        await this.loadNotesListAccountStatus();
                        if (!this.isNotesListLinked) {
                            this.loadNotesListLinkAccess();
                            if (this.isNotesListAccountLinked) {
                                this.loadLinkableNotesListProductionsInline();
                            }
                        }
                    }
                }
            });
            NavBus.$on('project-settings-nav.open.interval', (cb) => {
                if (this.$refs.projectSettingsNav) {
                    NavBus.$emit('project-settings-nav.open');
                    cb();
                }
            });
        },
        components: {
            'use-show-pass': UseShowPass
        },
        beforeDestroy: function() {
            NavBus.$off('project-settings-nav.open');
            NavBus.$off('project-settings-nav.open.interval');
        }
    };
    export default ProjectSettingsNav;
    
    </script>
    
    <style lang="scss">
    /* --- Show Sync (Notes List) status ---------------------------------------
       Badge colours are copied from staging-v2-main's .noteslist-account-badge
       so the two builds render identically once they merge. .noteslist-section
       reproduces md-layout's md-flex="90" md-flex-offset="5" geometry without
       pulling in another vue-material component. */
    .showpass-section {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: 90%;
        margin-left: 5%;
        margin-top: 1rem;
    }
    .showpass-line {
        margin: 0;
        line-height: 1.4;
    }
    .noteslist-section {
        display: flex;
        flex-direction: column;
        width: 90%;
        margin-left: 5%;
        margin-top: 1rem;
    }
    .noteslist-status-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 0px;
    }
    .noteslist-account-badge {
        display: inline-block;
        padding: 4px 10px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        border-radius: 999px;
        white-space: nowrap;

        &.is-linked {
            color: #1b5e20;
            background: #e8f5e9;
        }

        &.is-disconnected {
            color: #6d4c00;
            background: #fff8e1;
        }

        /* Not on staging yet: staging surfaces sync failures through a separate
           watcher/toast rather than the badge. Kept visually distinct from the
           amber "not connected" state so a failure never reads as merely idle. */
        &.is-error {
            color: #7f1d1d;
            background: #fdecea;
        }
    }
    .noteslist-link-loading {
    margin-top: 8px;
    font-size: 13px;
    opacity: 0.75;
}
.noteslist-connect-copy {
    margin-top: 4px;
}
.noteslist-production-picker {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
}
.noteslist-production-select {
    padding: 6px 8px;
    font-size: 14px;
    border: 1px solid rgba(0, 0, 0, 0.24);
    border-radius: 4px;
    background: #fff;
    color: rgba(0, 0, 0, 0.87);
}
.noteslist-link-hint {
    margin-top: 8px;
    opacity: 0.75;
}
.noteslist-subscription-alert {
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 4px;
    color: #6d4c00;
    background: #fff8e1;

    .noteslist-subscription-alert-message {
        margin: 4px 0 0;
    }
}
/* Production's button treatment, adopted 27 Aug on Josh's QA note so this panel
   matches the rest of project settings. Costs two <md-button>s in Stage D. */
@keyframes noteslist-btn-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
.noteslist-btn-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}
.noteslist-btn-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(13, 39, 68, 0.2);
    border-top-color: #0d2744;
    border-radius: 50%;
    animation: noteslist-btn-spin 0.7s linear infinite;
    flex-shrink: 0;
}
.noteslist-integration-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
    /* Whitespace under the buttons: without it they sit flush on the bottom edge
       of the sidenav, which reads as clipped. */
    margin-bottom: 28px;

    .md-button {
        margin: 0;
    }
}
.noteslist-connection-actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin-top: 12px;
    margin-bottom: 28px;

    .md-button {
        margin: 0;
    }
}
.noteslist-or-divider {
    align-self: center;
    font-size: 0.95rem;
    color: rgba(0, 0, 0, 0.65);
    margin: 0;
}
.noteslist-sync-error {
        color: #7f1d1d;
        margin-top: 4px;
    }
    .delete-section {
        margin-top: 20px;
    }
    .page-actions {
        position: relative;
        margin-top: 36px;
        .md-title {
            margin-left: 16px;
        }
        .working-backdrop {
            position: absolute;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.3);
            z-index: 8;
        }
        .page-action-icon {
            height: 48px;
            margin-right: 1em;
        }
    }
    .page-actions-list {
        padding-top: 0;
        .md-list-item-container.md-button {
            padding-top: 8px;
            padding-bottom: 8px;
        }
        .md-subheader {
            font-size: 18px;
            color: #333;
            &.md-theme-show-mode {
                color: rgba(255, 255, 255, 0.87);
            }
        }
        .page-action-form {
            form {
                .md-button {
                    margin-left: 0;
                }
                .input_page_number {
                    width: 40px;
                    font-size: 14px;
                    text-align: center;
                }
                .page-action-fields {
                    margin: 8px 16px;
                }
                margin-bottom: 12px;
            }
        }
    }
    .md-switch {
        &.md-checked {
            .md-switch-container {
                background-color: darken(#5ecfdf, 30%) !important;

                .md-switch-thumb {
                    background-color: #5ecfdf !important;
                }
            }
        }
        label {
            font-weight: 100;
        }
    }
    .project-cue-placement-form {
        .md-radio {
            margin: 0 0 8px;
        }
    }
    .projectWorkingBox {
        position: relative;
        z-index: 9;
        .projectWorkingBox-Message {
            position: absolute;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            width: 270px;
            text-align: center;
            background: #fff;
            border: solid 1px #333;
            font-size: 12px;
            padding: 10px;
            cursor: pointer;
        }


    }

    .md-dialog-container.md-active {
            z-index: 9999;
        }
    /* Disconnect-confirmation dialog. Ported from staging-v2-main: this
       component's markup uses .noteslist-disconnect-dialog but sandbox never
       carried the matching rules across, so the dialog rendered unstyled. */
    .noteslist-disconnect-dialog {
        .md-dialog {
            border-radius: 12px;
            width: 420px;
            max-width: calc(100vw - 32px);
            box-shadow: 0 12px 40px rgba(13, 39, 68, 0.25);
            overflow: hidden;
        }
        .md-dialog-title {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 24px 24px 0;
            color: #0d2744;
            font-size: 19px;
            font-weight: 600;
            line-height: 1.3;

            &::before {
                content: "warning";
                font-family: "Material Icons";
                font-size: 20px;
                flex: 0 0 36px;
                width: 36px;
                height: 36px;
                border-radius: 50%;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                color: #c77800;
                background: #fff8e1;
            }
        }
        .md-dialog-content {
            padding: 12px 24px 24px;
            color: rgba(13, 39, 68, 0.72);
            font-size: 14px;
            line-height: 1.55;
        }
        .md-dialog-actions {
            padding: 12px 20px 20px;
            gap: 8px;

            .md-button {
                margin: 0;
                min-width: 96px;
                border-radius: 6px;

                &:first-child {
                    color: rgba(13, 39, 68, 0.65);
                }

                &:last-child {
                    background: #c62828;
                    color: #fff !important;

                    &:hover {
                        background: #ad2323;
                    }
                }
            }
        }

        // Dark Mode class bound on the confirm component
        &.show-md-dark-mode {
            .md-dialog-title {
                color: #e8eef5;
            }
            .md-dialog-content {
                color: rgba(232, 238, 245, 0.85);
            }
            .md-dialog-actions .md-button:first-child {
                color: rgba(232, 238, 245, 0.75);
            }
        }
    }
    .md-theme-show-mode .noteslist-disconnect-dialog {
        .md-dialog-title {
            color: #e8eef5;
        }
        .md-dialog-content {
            color: rgba(232, 238, 245, 0.85);
        }
        .md-dialog-actions .md-button:first-child {
            color: rgba(232, 238, 245, 0.75);
        }
    }
    </style>


// WEBPACK FOOTER //
// nav.vue?5ed9108c